import React, { useState, useEffect, useContext, useCallback } from "react";
import { AuthContext } from "../context/AuthContext";
import { post, get } from "aws-amplify/api";

export default function CommentSection({ articleId }) {
  const { user } = useContext(AuthContext);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const fetchComments = useCallback(async () => {
    if (!articleId) return;
    
    setIsLoading(true);
    try {
      const response = await get({
        apiName: 'userApi',
        path: `/comments/${articleId}`
      });
      
      setComments(response.body.comments || []);
    } catch (error) {
      console.error("Error fetching comments:", error);
    } finally {
      setIsLoading(false);
    }
  }, [articleId]);
  
  useEffect(() => {
    fetchComments();
  }, [fetchComments]);
  
  const handleSubmitComment = async (e) => {
    e.preventDefault();
    
    if (!user) {
      alert("Please sign in to comment");
      return;
    }
    
    if (!newComment.trim()) return;
    
    setIsSubmitting(true);
    try {
      const response = await post({
        apiName: 'userApi',
        path: '/comments',
        options: {
          body: {
            articleId,
            content: newComment
          }
        }
      });
      
      // Add the new comment to the list
      const addedComment = response.body;
      setComments([...comments, addedComment]);
      setNewComment("");
    } catch (error) {
      console.error("Error posting comment:", error);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };
  
  return (
    <div className="mt-8 border-t pt-6">
      <h3 className="text-xl font-serif mb-4">Comments</h3>
      
      {/* Comment form */}
      {user && (
        <form onSubmit={handleSubmitComment} className="mb-6">
          <textarea
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Add a comment..."
            className="w-full p-3 border rounded-lg focus:ring-1 focus:ring-taupe focus:border-taupe"
            rows="3"
            disabled={isSubmitting}
          ></textarea>
          <button
            type="submit"
            disabled={isSubmitting || !newComment.trim()}
            className="mt-2 px-4 py-2 bg-taupe text-white rounded-lg hover:bg-taupe-dark disabled:opacity-50"
          >
            {isSubmitting ? "Posting..." : "Post Comment"}
          </button>
        </form>
      )}
      
      {/* Comments list */}
      {isLoading ? (
        <div className="text-center py-4">Loading comments...</div>
      ) : comments.length > 0 ? (
        <div className="space-y-4">
          {comments.map((comment) => (
            <div key={comment.id} className="border-b pb-4">
              <div className="flex items-center mb-2">
                <div className="font-medium">{comment.authorName}</div>
                <div className="text-sm text-gray-500 ml-2">
                  {formatDate(comment.createdAt)}
                </div>
              </div>
              <div className="text-gray-700">{comment.content}</div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-4 text-gray-500">
          No comments yet. Be the first to comment!
        </div>
      )}
      
      {/* Sign in prompt */}
      {!user && (
        <div className="text-center py-4 border rounded-lg bg-gray-50">
          <p className="mb-2">Sign in to join the conversation</p>
          <button 
            onClick={() => window.location.href = "https://actadiurna.auth.eu-west-2.amazoncognito.com/login?client_id=4bi8nq055s0vrr43s3qc76p8jk&response_type=token&scope=email+openid+profile&redirect_uri=https%3A%2F%2Factadiurna.org.uk%2F"}
            className="px-4 py-1 bg-taupe text-white rounded-lg hover:bg-taupe-dark"
          >
            Sign In
          </button>
        </div>
      )}
    </div>
  );
}